<template>
  <div class="bui-entity-listing-page player-editor-redesign">
  <content-with-loading-skeleton
    :isLoading="isPlayerLoading"
    loadingMessage="Loading Player Data..."
    :isWithMessage="true"
  >
    <template #loaded>
      <div v-if="selectedPlayer" class="player-editor-redesign__inner">
        <!-- Page header -->
        <header class="pe-header">
          <div class="pe-header__left">
            <div class="pe-header__title-row">
              <h1 class="bui-page-title">Player Editor</h1>
              <button
                type="button"
                class="pe-header__refresh"
                aria-label="Refresh player data"
                @click="loadPlayer(playerId)"
              >
                <PhArrowsClockwise :size="22" weight="bold" />
              </button>
            </div>
            <div class="pe-header__subtitle">{{ selectedPlayer?.name }}</div>
          </div>
          <div class="pe-header__right">
            <LocationInfoDialog
              :location="selectedPlayer?.location"
              :readOnly="isReadOnly"
              @change="changeLocation($event)"
            />
            <PlayerPreview :playerId="selectedPlayer.id" :playerName="selectedPlayer.name" />
          </div>
        </header>

        <!-- Stat tiles row -->
        <section class="pe-stats" aria-label="Player summary">
          <StatTile
            :icon="PhMonitorPlay"
            label="Player Version"
            :value="selectedPlayer?.playerVersion || '—'"
            :subValue="dateVersionUpdatedLabel || undefined"
          />
          <StatTile
            :icon="PhClockCountdown"
            label="Last Connected"
            :value="lastConnectedLabel"
          />
          <StatTile
            :icon="PhMapPin"
            label="Last Known IP"
            :value="selectedPlayer?.ipAddress || '—'"
            :subValue="ipUpdatedLabel || undefined"
          />
          <StatTile
            :icon="PhArrowsClockwise"
            label="Orientation"
            :value="orientationLabel"
          />
          <StatTile
            :icon="metricsBundle.isTimedOut.value ? PhWarningCircle : PhCheckCircle"
            label="Status"
            :value="metricsBundle.statusText.value"
            :subValue="metricsBundle.statusDetail.value || undefined"
            :tone="metricsBundle.isTimedOut.value ? 'warning' : 'good'"
          />
        </section>

        <!-- Two-column editor grid -->
        <section class="editor-grid">
          <!-- LEFT COLUMN -->
          <div class="editor-col">
            <!-- Player Details -->
            <InfoCard>
              <template #title>
                <h2 class="info-card__title-text">Player Details</h2>
              </template>
              <template #actions>
                <div class="d-flex align-items-center" style="gap: 0.5rem;">
                  <ReassignPlayerForm
                    v-if="user.isAccountAdmin() && !isReadOnly"
                    :playerId="selectedPlayer.id"
                  />
                  <button
                    v-if="!isReadOnly"
                    type="button"
                    class="pe-edit-toggle"
                    :class="{ 'pe-edit-toggle--active': isEditing }"
                    @click="isEditing = !isEditing"
                  >
                    <PhPencilSimple :size="14" weight="bold" />
                    <span>{{ isEditing ? 'Done' : 'Edit' }}</span>
                  </button>
                </div>
              </template>

              <StatusBar
                :errorMessage="errorMessage"
                :isLoading="isLoading"
                loadingMessage="Saving..."
                :isSuccess="isSuccess"
                successMessage="Saved!"
              />

              <div class="field-content">
                <div class="d-flex field-row">
                  <label for="name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
                  <TextField
                    v-model="values.name"
                    class="basis-1/2"
                    width="100%"
                    :disabled="!isEditing || isReadOnly"
                  />
                </div>
                <div class="d-flex field-row">
                  <label for="Description" class="basis-1/2 text-sm font-bold">Description</label>
                  <TextField
                    width="100%"
                    v-model="values.description"
                    class="basis-1/2"
                    :disabled="!isEditing || isReadOnly"
                  />
                </div>
                <div class="d-flex field-row">
                  <label class="basis-1/2 text-sm font-bold">Device Model</label>
                  <label class="basis-1/2 text-sm">{{ selectedPlayer?.deviceType?.model || '—' }}</label>
                </div>
                <div v-if="route.query.showUid === 'true'" class="d-flex field-row">
                  <label class="basis-1/2 text-sm font-bold">Device UUID</label>
                  <label class="basis-1/2 text-sm">{{ selectedPlayer?.deviceUuid }}</label>
                </div>
                <div class="d-flex field-row">
                  <label class="basis-1/2 text-sm font-bold">Capabilities</label>
                  <label class="basis-1/2 text-sm">
                    <span v-for="mime of selectedPlayer?.deviceType?.mimeTypes" :key="mime.name">
                      {{ mime.name }}{{ ' ' }}
                    </span>
                    <span v-if="!selectedPlayer?.deviceType?.mimeTypes?.length">—</span>
                  </label>
                </div>
                <div class="d-flex field-row">
                  <label class="basis-1/2 text-sm font-bold">Date Registered</label>
                  <label class="basis-1/2 text-sm">
                    <TimeFormatDateOnly :epochTime="selectedPlayer?.dateRegistered" />
                  </label>
                </div>
                <div v-if="!isReadOnly" class="d-flex field-row">
                  <label class="basis-1/2 text-sm font-bold">Sync / Split Config</label>
                  <div class="basis-1/2 text-sm">
                    <SyncSplitConfig v-model="values.syncConfig" />
                  </div>
                </div>
                <div v-if="!isReadOnly" class="d-flex field-row">
                  <label class="basis-1/2 text-sm font-bold">Change Orientation</label>
                  <label class="basis-1/2 text-sm">
                    <OrientationEditor
                      @save="changeOrientation($event)"
                      :orientation="selectedPlayer.orientation"
                      :title="selectedPlayer.name"
                    />
                  </label>
                </div>
              </div>
            </InfoCard>

            <!-- Tags -->
            <InfoCard flush>
              <template #title>
                <h2 class="info-card__title-text">Tags</h2>
              </template>
              <template #actions>
                <AddTag
                  v-if="!isReadOnly"
                  :player="selectedPlayer"
                  @save="addTag($event)"
                />
              </template>
              <div class="pe-table-wrap">
                <table class="pe-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Value</th>
                      <th>Data Type</th>
                      <th>Last Modified</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(metadata, index) of selectedPlayer.metaDatas" :key="metadata.id">
                      <tr v-if="!metadata.isDeleted">
                        <td><span class="tag-pill">{{ metadata.name }}</span></td>
                        <td>{{ metadata.value }}</td>
                        <td>{{ metadata.datatype }}</td>
                        <td><TimeFormatDateOnly :epochTime="metadata.createdDate" /></td>
                        <td class="pe-table__actions">
                          <DeleteTag
                            v-if="!isReadOnly"
                            @save="deleteTag(metadata.id, index)"
                          />
                        </td>
                      </tr>
                    </template>
                    <tr v-if="visibleTagsCount === 0">
                      <td colspan="5" class="pe-table__empty">No tags yet</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="pe-table-footer">
                Showing {{ visibleTagsCount }} of {{ visibleTagsCount }} tag{{ visibleTagsCount === 1 ? '' : 's' }}
              </div>
            </InfoCard>

            <!-- Player Subscriptions -->
            <InfoCard flush>
              <template #title>
                <h2 class="info-card__title-text">Player Subscriptions</h2>
              </template>
              <div class="pe-table-wrap">
                <table class="pe-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="playerSubscriptions.length > 0">
                      <tr v-for="subscription of playerSubscriptions" :key="subscription.Id">
                        <td>
                          <RouterLink
                            v-if="!isReadOnly"
                            :to="getSubscriptionLink(subscription)"
                            class="campaign-link"
                          >
                            {{ subscription.Name }}
                          </RouterLink>
                          <span v-else>{{ subscription.Name }}</span>
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr>
                        <td class="pe-table__empty">No player subscriptions</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
              <div class="pe-table-footer">
                Showing {{ playerSubscriptions.length }} of {{ playerSubscriptions.length }} subscription{{ playerSubscriptions.length === 1 ? '' : 's' }}
              </div>
            </InfoCard>

            <!-- Device Metrics -->
            <InfoCard v-if="user.isAnalytics()">
              <template #title>
                <h2 class="info-card__title-text">Device Metrics</h2>
                <PhInfo
                  :size="14"
                  weight="regular"
                  class="pe-title-info"
                  aria-hidden="true"
                />
              </template>
              <PlayerMetrics :bundle="metricsBundle" />
            </InfoCard>
          </div>

          <!-- RIGHT COLUMN -->
          <div class="editor-col">
            <!-- Assigned Campaigns -->
            <InfoCard flush>
              <template #title>
                <h2 class="info-card__title-text">Assigned Campaigns</h2>
              </template>
              <template #actions>
                <AddCampaign
                  v-if="!isReadOnly"
                  @save="addCampaign($event)"
                  :playerTitle="selectedPlayer.name"
                />
              </template>
              <div class="pe-table-wrap">
                <table class="pe-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Organization</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th class="pe-table__actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(campaign, index) of selectedPlayer.campaigns" :key="campaign.id">
                      <tr>
                        <td>
                          <RouterLink
                            :to="{ name: 'campaign-editor', params: { id: campaign.id } }"
                            class="campaign-link"
                          >
                            {{ campaign.name }}
                          </RouterLink>
                        </td>
                        <td>{{ campaign.groupName || '—' }}</td>
                        <td><TimeFormatDateOnly :epochTime="campaign.startDate" /></td>
                        <td><TimeFormatDateOnly :epochTime="campaign.endDate" /></td>
                        <td class="pe-table__actions">
                          <button
                            v-if="!isReadOnly"
                            type="button"
                            class="pe-row-action-btn"
                            aria-label="Remove campaign"
                            @click.stop="deleteCampaign(index)"
                          >
                            <PhTrash :size="16" weight="bold" />
                          </button>
                        </td>
                      </tr>
                    </template>
                    <tr v-if="!selectedPlayer.campaigns?.length">
                      <td colspan="5" class="pe-table__empty">No campaigns assigned</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="pe-table-footer">
                Showing {{ selectedPlayer.campaigns?.length || 0 }} of {{ selectedPlayer.campaigns?.length || 0 }} campaign{{ (selectedPlayer.campaigns?.length || 0) === 1 ? '' : 's' }}
              </div>
            </InfoCard>

            <!-- Uptime -->
            <InfoCard v-if="user.isAnalytics()">
              <template #title>
                <h2 class="info-card__title-text">Uptime</h2>
                <PhInfo
                  :size="14"
                  weight="regular"
                  class="pe-title-info"
                  aria-hidden="true"
                />
              </template>
              <UptimeSearch
                :lastConnectTime="selectedPlayer.lastConnectTime"
                :playerId="playerId"
                hideHeader
              />
            </InfoCard>
          </div>
        </section>

        <!-- Sticky footer -->
        <footer class="editor-footer">
          <div class="editor-footer__inner">
            <ButtonCustomized
              v-if="!hasChanges || isReadOnly"
              label="Exit"
              ctaSecondary
              @click="onExit"
            />
            <BeforeExitDialogue
              v-if="hasChanges && !isReadOnly"
              @save="submitAndExit"
              @exit="onExit"
            />
            <ButtonCustomized
              v-if="!isReadOnly"
              label="Save"
              cta
              :disabled="!hasChanges"
              @click="onSubmit"
            />
            <Saving ref="savingDialog" />
          </div>
        </footer>
      </div>
    </template>
  </content-with-loading-skeleton>
  </div>
</template>
<script  lang="ts" setup>
import usePlayerEditorForm from './PlayerEditorViewHelper';
import usePlayerMetrics from './PlayerMetrics/usePlayerMetrics';
import { computed, onBeforeMount, onUnmounted, ref } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import {
    PhArrowsClockwise,
    PhMonitorPlay,
    PhClockCountdown,
    PhMapPin,
    PhWarningCircle,
    PhCheckCircle,
    PhPencilSimple,
    PhInfo,
    PhTrash,
} from '@phosphor-icons/vue';
import useNotifications from "@/components/Notifications/composables/notifications";
import useUser from '@/composables/user';
import ButtonCustomized from '@/components/form-fields/ButtonCustomized.vue';
import Saving from '@/components/Dialog/SavingDialog/Saving.vue'
import BeforeExitDialogue from '@/components/Dialog/BeforeExitDialog/BeforeExitDialogue.vue'
import TextField from "@/components/form-fields/TextField.vue";
import LocationInfoDialog from './LocationInfo/LocationInfoDialog.vue';
import TimeFormatDateOnly from '@/components/ListingPages/ItemsLister/ItemsList/FieldsRenderers/TimeFormatDateOnly.vue'
import router from '@/router';
import AddTag from './Tags/AddTag.vue'
import DeleteTag from './Tags/DeleteTag.vue'
import AddCampaign from './AddCampaign/AddCampaignDialog.vue';
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import PlayerPreview from './PlayerPreview/PlayerPreview.vue';
import OrientationEditor from './OrientationEditor/OrientationEditor.vue';
import UptimeSearch from './UptimeSearch/UptimeSearch.vue';
import ReassignPlayerForm from './ReassignPlayer/ReassignPlayerForm.vue'
import SyncSplitConfig from './SyncSplitConfig/SyncSplitConfig.vue';
import PlayerMetrics from './PlayerMetrics/PlayerMetrics.vue';
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';
import StatTile from './StatTile/StatTile.vue';
import { orientationOptions } from '@/data/options/orientationOptions';

const route = useRoute()
const notification = useNotifications();
const savingDialog = ref()
const user = useUser();
const isReadOnly = computed(() => !user.hasWriteAccessToArea('Players'));
const isEditing = ref(false);
const { loadPlayer, playerId,
    isLoading,
    isPlayerLoading,
    errorMessage,
    selectedPlayer, deleteTag, values, hasChanges, addTag, submitForm,
    isSuccess, removeBeforeLoad, playerSubscriptions } = usePlayerEditorForm();

// Page-level metrics bundle shared by the Status stat tile and the Device Metrics card.
const metricsBundle = usePlayerMetrics({
    deviceUuid: () => selectedPlayer.value?.deviceUuid,
    pollMs: 10_000,
});

onBeforeMount(() => {
    loadPlayer(route.params.id)
})
function changeLocation(newLocation: any) {
    hasChanges.value = true
    selectedPlayer.value.location = newLocation
}
onUnmounted(() => {
    removeBeforeLoad()
})
onBeforeRouteLeave(() => {
    if (hasChanges.value) {
        const answer = window.confirm(
            'Changes you made may not be saved.'
        )
        if (!answer) return false

    }
})


function onExit() {
    hasChanges.value = false
    isEditing.value = false
    router.push({ path: '/players' })
}
async function submitAndExit() {
    savingDialog.value.open()
    await submitForm();
    savingDialog.value.close()
    if (isSuccess.value === true) {
        notification.success('Player updated successfully!');
        removeBeforeLoad();
        isEditing.value = false
        router.push({ path: '/players' })
    }
}
async function onSubmit() {
    savingDialog.value.open()
    await submitForm();
    savingDialog.value.close()
    if (isSuccess.value === true) {
        removeBeforeLoad();
        isEditing.value = false
        notification.success('Player updated successfully!');
    }
}
function addCampaign(campaignItems: any[]) {
    campaignItems.forEach(item => {
        if (selectedPlayer.value.campaigns.filter((campaign: any) => campaign.id == item.id).length == 0) {
            hasChanges.value = true
            selectedPlayer.value.campaigns.push(item)

        }
    })
}
function deleteCampaign(index: number) {
    hasChanges.value = true
    selectedPlayer.value.campaigns.splice(index, 1)
}
function changeOrientation(value: number) {
    hasChanges.value = true
    selectedPlayer.value.orientation = value
}

function getSubscriptionLink(subscription: any) {
    const isLiveStreamSubscription = subscription.bindings?.some((binding: any) =>
        binding?.implementationKey === 'liveStream'
    );

    if (isLiveStreamSubscription) {
        return {
            name: 'live-stream-editor',
            params: { id: subscription.Id }
        };
    } else {
        return {
            name: 'player-subscription-editor',
            params: { id: subscription.Id }
        };
    }
}

// --- Derived display values for header / stat tiles -----------------------

function formatEpochDate(epoch: number | string | null | undefined): string {
    if (epoch == null || epoch === '') return '—';
    const n = typeof epoch === 'string' ? Number(epoch) : epoch;
    const d = new Date(n);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatEpochDateTime(epoch: number | string | null | undefined): string {
    if (epoch == null || epoch === '') return '—';
    const n = typeof epoch === 'string' ? Number(epoch) : epoch;
    const d = new Date(n);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit'
    });
}

const dateVersionUpdatedLabel = computed(() => {
    const formatted = formatEpochDate(selectedPlayer.value?.dateVersionUpdated);
    return formatted === '—' ? '' : `Updated ${formatted}`;
});

const ipUpdatedLabel = computed(() => {
    const formatted = formatEpochDate(selectedPlayer.value?.dateIpLastUpdated);
    return formatted === '—' ? '' : `Updated ${formatted}`;
});

const lastConnectedLabel = computed(() =>
    formatEpochDateTime(selectedPlayer.value?.lastConnectTime)
);

const orientationLabel = computed(() => {
    const v = selectedPlayer.value?.orientation;
    if (v == null) return '—';
    const match = orientationOptions.find((o) => o.value === v);
    return match?.name ?? String(v);
});

const visibleTagsCount = computed(
    () => (selectedPlayer.value?.metaDatas || []).filter((m: any) => !m.isDeleted).length
);
</script>
<style lang="scss" scoped>
/* Outer wrapper. The `.bui-entity-listing-page` class is wired in
   PageLayout.vue (`.content:has(> .bui-entity-listing-page)`) to paint the
   magenta page surface edge-to-edge between the sidebar and the viewport.
   We repeat the magenta background here as a defensive fallback in case the
   page is ever rendered outside `PageLayout`. Padding is also applied here so
   the loading skeleton sits on the magenta surface with the right margins. */
.player-editor-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  /* Squeeze padding a bit on narrower viewports (also when sidebar expanded). */
  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

/* Inner column rendered inside `content-with-loading-skeleton`'s `.fade-item`
   transition wrapper. Holds the page's flex layout (gap between sections). */
.player-editor-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

/* ---- Page header ---- */
.pe-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.pe-header__left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.pe-header__title-row {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
}

.bui-page-title {
  margin: 0;
  font-family: 'Robout', system-ui, sans-serif;
  font-weight: 300;
  font-size: var(--bui-page-head-size, 31px);
  line-height: var(--bui-page-head-line, 31px);
  color: var(--bui-page-head, #fffcfd);
}

.pe-header__refresh {
  /* Mirrors the BUI tertiary 52px pill icon-only variant used in TblTopBar. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--bui-ter-fill, rgba(246, 247, 255, 0.16));
  border: 1px solid var(--bui-ter-border, rgba(246, 247, 255, 0.04));
  color: var(--bui-ter-icon, #fffcfd);
  border-radius: var(--bui-ter-radius, 8px);
  cursor: pointer;

  &:hover { background: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28)); }
}

.pe-header__subtitle {
  /* Muted magenta-surface text matches `--bui-tbl-detail-text`. */
  color: var(--bui-tbl-detail-text, #ffb2db);
  font-size: 0.95rem;
  font-weight: 500;
}

.pe-header__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  /* Make `PlayerPreview`'s internal `<ButtonCustomized secondary>` look like a
     BUI tertiary pill on the magenta surface so it sits beside `LocationInfoDialog`
     without standing out. */
  :deep(.button) {
    background-color: var(--bui-ter-fill, rgba(246, 247, 255, 0.16));
    color: var(--bui-ter-text, #ffd9e6);
    border: 1px solid var(--bui-ter-border, rgba(246, 247, 255, 0.04));
    border-radius: var(--bui-ter-radius, 8px);
    text-transform: none;
    font-weight: 500;
    height: var(--bui-cta-h, 52px);
    box-shadow: none;
    padding: 0 var(--bui-ter-pad, 14px);

    &:hover {
      background-color: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28));
    }
  }
}

/* ---- Stat tile row ---- */
.pe-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

/* ---- Two column grid ---- */
.editor-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.editor-col {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

/* ---- Player Details fields ---- */
.field-content {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  :deep(.field label) {
    color: var(--bui-tbl-row-text, #2a2a30);
  }

  :deep(.field input[type='text']),
  :deep(.field textarea) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
  }
}

.field-row {
  align-items: center;

  label {
    color: var(--bui-tbl-row-text, #2a2a30);
  }
}

/* ---- Title-row accents ---- */
.pe-title-info { color: var(--bui-tbl-detail-text, #5c5c66); }

/* ---- EDIT toggle ----
 * Inactive: BUI rose tag (rose fill + violet text).
 * Active:   BUI grape pill (grape fill + cream text).
 */
.pe-edit-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: var(--bui-badge-tag-fill, #ffd9e6);
  border: 0;
  border-radius: var(--bui-badge-radius, 100px);
  padding: 4px 12px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--bui-badge-tag-text, #a420c8);
  text-transform: uppercase;
  transition: background-color 120ms ease;

  &:hover { filter: brightness(0.97); }

  &--active {
    background: var(--bui-tbl-head-fill, #2a2a30);
    color: var(--bui-tbl-head-text, #f2f2f4);
  }
}

/* ---- Tables (BUI flavoured) ---- */
.pe-table-wrap {
  overflow-x: auto;
}

.pe-table {
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
  font-size: 0.875rem;
  color: var(--bui-tbl-row-text, #2a2969);

  thead th {
    background: var(--bui-tbl-head-fill, #a420c8);
    color: var(--bui-tbl-head-text, #fffcfd);
    font-size: var(--bui-tbl-head-font-size, 12px);
    line-height: var(--bui-tbl-head-line-height, 14.4px);
    letter-spacing: var(--bui-tbl-head-letter-spacing, 0.48px);
    text-transform: uppercase;
    font-weight: 600;
    text-align: left;
    height: 40px;
    padding: 0 var(--bui-tbl-cell-pad-x, 16px);
    white-space: nowrap;
  }

  thead th:first-child {
    border-top-left-radius: 4px;
  }
  thead th:last-child {
    border-top-right-radius: 4px;
  }

  tbody td {
    padding: 0.75rem var(--bui-tbl-cell-pad-x, 16px);
    border-bottom: 1px solid var(--bui-tbl-line, #ffd9e6);
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover td {
    background: var(--bui-tbl-rowbtn-hover-fill, #ececf0);
  }
}

.pe-table__actions-col,
.pe-table__actions {
  width: 1%;
  text-align: right;
}

.pe-row-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--bui-tbl-rowbtn-size, 28px);
  height: var(--bui-tbl-rowbtn-size, 28px);
  padding: 0;
  border: 0;
  border-radius: var(--bui-tbl-rowbtn-radius, 32px);
  background: transparent;
  color: var(--bui-tbl-rowbtn-color, #2a2a30);
  cursor: pointer;

  &:hover {
    background: var(--bui-tbl-rowbtn-hover-fill, #ececf0);
  }

  &:focus-visible {
    outline: 2px solid var(--bui-chk-checked-fill, #2a2a30);
    outline-offset: 2px;
  }
}

.pe-table__empty {
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-style: italic;
  text-align: center;
}

.tag-pill {
  display: inline-block;
  background: var(--bui-badge-tag-fill, #ffd9e6);
  color: var(--bui-badge-tag-text, #a420c8);
  border-radius: var(--bui-badge-radius, 100px);
  padding: 2px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pe-table-footer {
  padding: 0.625rem var(--bui-tbl-cell-pad-x, 16px);
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.75rem;
  border-top: 1px solid var(--bui-tbl-line, #ffd9e6);
}

.campaign-link {
  color: var(--bui-tbl-row-link, #3a3a44);
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;

  &:visited {
    color: var(--bui-tbl-row-link-visited, var(--bui-tbl-row-link, #3a3a44));
  }

  &:hover {
    color: var(--bui-tbl-row-link-hover, var(--bui-tbl-row-link, #3a3a44));
    text-decoration: underline;
  }
}

/* ---- Sticky footer ---- */
.editor-footer {
  position: fixed;
  bottom: 1rem;
  right: 1.5rem;
  z-index: 5;
}

.editor-footer__inner {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  gap: 0.75rem;
  background: var(--bui-tbl-row-fill, #fffcfd);
  border-radius: 999px;
  padding: 0.5rem 0.75rem;
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));

  /* Dialog expander wrappers (Exit confirmation) should hug their button. */
  :deep(> .flex) {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
  }

  :deep(.button) {
    border-radius: 999px;
    min-height: 2.5rem;
    box-shadow: none;
  }
}
</style>